// GET /bands/{bandId}/tasks/assignable — who the caller may hand a task to on this band.
//
// THE WRITE GATE, READ FORWARDS. The rows come from core.assignable_people, the same function the save
// filters on, so the assignee picker offers exactly what the create/assign routes accept. It replaced a read
// of the band TEAM, which disagreed with the gate in both directions: an org staffer was never offered (an
// org seat is not a band seat) however plainly the gate would have taken them, and a seat with no assign
// edges — an intern — was offered the whole crew with every pick refused.
//
// It spans BAND and ORG seats, which is what makes an audience mean anything: an `assign` edge to
// `management` buys you the management people who reach THIS band.
import { useMemo } from "react";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useQuery } from "@tanstack/react-query";
import { useBandContextOrNull } from "../bandContext.js";
import { roleLabelFrom, useSeatRoles } from "./useSeatRoles.js";
import { ApiError } from "./apiError.js";

export type AssignableRead = components["schemas"]["AssignableRead"];
export type TaskAssigneeCandidate = components["schemas"]["TaskAssigneeCandidate"];
export type InvitablePeopleRead = components["schemas"]["InvitablePeopleRead"];

/**
 * The band's assignable people. `staleTime` matches the crew read: the set changes when seats or org
 * assignments change, which is rare inside a session, and a stale row costs nothing — the write path
 * re-checks the same function, so an offered row that has since become invalid is refused, not written.
 */
export function useAssignable(options: { enabled?: boolean } = {}) {
  const { api } = useApp();
  // CONTEXT-SAFE, and `enabled` was not enough on its own. This went through `useBandQuery`, which reads the
  // THROWING band context — and a hook throws whether or not its query is disabled. `CrewInviteePicker`
  // calls this unconditionally and now has a source that mounts with no band (the inbox, opened from the
  // topbar, which sits outside the artist shell's own provider), so the disabled read took the whole dialog
  // down with "useBandContext() requires a mounted <BandProvider>". The CrewProfileModal rule verbatim.
  //
  // The KEY is unchanged (`['band', bandId, 'tasks', 'assignable']`), so this still shares one cache entry
  // with every other caller — a different key here would be a second copy of the same list.
  const band = useBandContextOrNull();
  const bandId = band?.bandId;
  const enabled = (options.enabled ?? true) && !!bandId;
  return useQuery<AssignableRead, Error>({
    queryKey: ["band", bandId, "tasks", "assignable"],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/tasks/assignable", {
        params: { path: { bandId: bandId as string } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    staleTime: 60_000,
    enabled,
  });
}

/**
 * The band's INVITABLE people (ART-176) — band crew ∪ the org (label/management/booking) seats reaching the
 * band, from `core.invitable_people`: the assignment gate's candidate set WITHOUT the task-assign edge
 * narrowing, because an invite is not an assignment (an intern or content seat can be asked to a rehearsal
 * though they cannot be handed a task). The row shape is identical to `useAssignable`, so it shares the
 * display normalizer below. Context-safe for the same reason `useAssignable` is.
 */
export function useInvitable(options: { enabled?: boolean } = {}) {
  const { api } = useApp();
  const band = useBandContextOrNull();
  const bandId = band?.bandId;
  const enabled = (options.enabled ?? true) && !!bandId;
  return useQuery<InvitablePeopleRead, Error>({
    queryKey: ["band", bandId, "opportunities", "invitable"],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/opportunities/invitable", {
        params: { path: { bandId: bandId as string } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    staleTime: 60_000,
    enabled,
  });
}

// ---- display normalization ------------------------------------------------------------------------------

/**
 * Headings for the candidate lists, keyed by policy ROOT. A crew member and a manager are indistinguishable
 * in a flat list of names, and the audience is the thing the gate actually reasons about — so it is what the
 * list is cut on. Label and Booking also name the COMPANY ("Label · Fernwood Records"), because an artist can
 * have more than one of each and "Label" alone does not say whose (product owner, ART-325).
 */
const ROOT_GROUPS: Record<string, string> = {
  artist: "Band",
  management: "Management",
  label: "Label",
  booking: "Booking",
};

/** The order the teams are listed in: the band first, then management, label, booking (ART-325). */
const ROOT_ORDER: Record<string, number> = { artist: 0, management: 1, label: 2, booking: 3 };

/** The group heading for one candidate. */
export function candidateGroup(root: string | null | undefined, orgName: string | null | undefined): string | undefined {
  const base = ROOT_GROUPS[root ?? ""];
  if (!base) return undefined;
  return (root === "label" || root === "booking") && orgName ? `${base} · ${orgName}` : base;
}

/** One candidate, resolved for display: a name, a face, the job they hold and the audience they sit under. */
export interface AssignableRow {
  personId: string;
  displayName: string;
  avatarMediaId: string | null;
  jobLabel: string;
  /** The granted role key, kept beside the resolved jobLabel so the picker can draw the AV mark (ART-171).
   *  The label is a plain string (title-resolved, band OR org); the key is what says "full access". */
  roleKey: string | null;
  isOwner: boolean;
  group: string | undefined;
}

/**
 * The assignable people, ready to render. TWO pickers list this set — the create modal's chip picker and the
 * drawer's "+ Add" — so the grouping and the job label live here rather than at each call site: the same
 * person must not read "Mgmt Day To Day" on one and "Day-to-Day Manager" on the other.
 *
 * ⚠️ THE CATALOG IS CHOSEN BY ROOT, never tried in turn. `roleLabelFrom` NEVER returns empty — it
 * title-cases an unknown key as a last resort — so a `bandRoles || orgRoles` chain always stops at the first
 * list and an org title resolved against the band vocabulary comes out plausible and wrong. That is the
 * exact failure its own doc names.
 *
 * The label resolves from the TITLE, not the role: 70 titles collapse onto 22 roles, so a job recovered from
 * a role names the wrong one ("Head of A&R" and "Marketing Associate" are both `label_staff`).
 */
export function useAssignableRows(options: { enabled?: boolean } = {}) {
  const candidates = useAssignable(options);
  const seatRoles = useSeatRoles();
  const rows = useCandidateRows(candidates.data?.items, seatRoles.data);
  return { rows, isLoading: candidates.isLoading, isError: candidates.isError };
}

/**
 * The INVITABLE people, ready to render (ART-176) — the same normalization as {@link useAssignableRows}, over
 * the opportunity-invitee set. The opportunity create modal's picker lists this; a manager and a crew member
 * are indistinguishable in a flat list, so the same root grouping + title-resolved job label apply.
 */
export function useInvitableRows(options: { enabled?: boolean } = {}) {
  const candidates = useInvitable(options);
  const seatRoles = useSeatRoles();
  const rows = useCandidateRows(candidates.data?.items, seatRoles.data);
  return { rows, isLoading: candidates.isLoading, isError: candidates.isError };
}

/** Shared candidate → display-row normalization. Assignable and invitable carry the identical row shape
 *  (person + role + title + root + is_owner), so the grouping and the title-resolved job label live here
 *  once — the same person must not read one job on the task picker and another on the opportunity picker. */
function useCandidateRows(
  items: readonly TaskAssigneeCandidate[] | null | undefined,
  seatRoles: ReturnType<typeof useSeatRoles>["data"],
): AssignableRow[] {
  return useMemo<AssignableRow[]>(
    () =>
      [...(items ?? [])]
        // Stable sort: the server's name order survives inside each team.
        .sort((a, b) => (ROOT_ORDER[a.root ?? ""] ?? 9) - (ROOT_ORDER[b.root ?? ""] ?? 9)
          || (a.orgName ?? "").localeCompare(b.orgName ?? ""))
        .flatMap((c) =>
        c.personId
          ? [
              {
                personId: c.personId,
                displayName: c.displayName || "Member",
                avatarMediaId: c.avatarMediaId ?? null,
                jobLabel: roleLabelFrom(
                  c.root === "artist" ? seatRoles?.bandRoles : seatRoles?.orgRoles,
                  c.titleKey,
                ),
                roleKey: c.role ?? null,
                isOwner: c.isOwner ?? false,
                group: candidateGroup(c.root, c.orgName),
              },
            ]
          : [],
      ),
    [items, seatRoles],
  );
}
